import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Navigation, MapPin, Play, CheckCircle2,
  ExternalLink, Leaf, Clock, Calendar, Loader2, Activity
} from 'lucide-react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  getLantanaClearingSchedule,
  startLantanaClearing,
  executeLantanaClearing,
} from '../../services/api'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'YOUR_MAPBOX_TOKEN_HERE'

// Map the schedule's assignment_status onto the same visual vocabulary the
// old patch-status-based view used, so stylistic tokens stay intact.
const STATUS_COLORS = {
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
}

const STATUS_LABELS = {
  assigned: 'Scheduled',
  in_progress: 'In Progress',
}

export default function ClearerFieldView({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [selectedScheduleId, setSelectedScheduleId] = useState(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [finishModal, setFinishModal] = useState(null)
  const [finishDate, setFinishDate] = useState(new Date().toISOString().split('T')[0])

  // The backend auto-scopes to assigned_to = self when the caller's role at
  // the ranch is bush_clearer, so the frontend doesn't need to pass userId.
  // A manager hitting this page still sees every row — by design; the page
  // is listed under MY_LANTANA_ASSIGNMENTS for the bush_clearer nav, but the
  // route itself is permissive.
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['clearingSchedule', ranchId],
    queryFn: () => getLantanaClearingSchedule(ranchId),
  })

  // Start clearing: flip assignment_status → 'in_progress' on the schedule row.
  // The patch itself keeps its current status — only /execute transitions the
  // patch to 'cleared'. This avoids the old dual-write drift where the patch
  // and schedule could disagree.
  const startMutation = useMutation({
    mutationFn: (scheduleId) => startLantanaClearing(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['clearingSchedule'])
      queryClient.invalidateQueries(['lantanaPatches'])
    },
  })

  // Finish clearing: the schedule's /execute endpoint commits BOTH the
  // schedule (status='completed', assignment_status='completed') and the
  // patch (status='cleared', cleared_by=caller) in one transaction.
  const finishMutation = useMutation({
    mutationFn: ({ scheduleId, endDate }) =>
      executeLantanaClearing(scheduleId, null, endDate),
    onSuccess: () => {
      queryClient.invalidateQueries(['clearingSchedule'])
      queryClient.invalidateQueries(['lantanaPatches'])
      setFinishModal(null)
    },
  })

  useEffect(() => {
    if (map.current || !mapContainer.current) return
    if (!MAPBOX_TOKEN || MAPBOX_TOKEN === 'YOUR_MAPBOX_TOKEN_HERE') return

    mapboxgl.accessToken = MAPBOX_TOKEN

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [32.5825, 0.3476],
      zoom: 14,
    })

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right')
    map.current.addControl(new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    }))

    map.current.on('load', () => setMapLoaded(true))

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!map.current || !mapLoaded || schedules.length === 0) return

    const layerIds = ['patches-fill', 'patches-outline', 'selected-fill', 'selected-outline']
    layerIds.forEach(id => {
      if (map.current.getLayer(id)) map.current.removeLayer(id)
    })
    if (map.current.getSource('patches')) map.current.removeSource('patches')
    if (map.current.getSource('selected')) map.current.removeSource('selected')

    const withCoords = schedules.filter(s =>
      s.boundary_coordinates && s.boundary_coordinates.length >= 3
    )

    if (withCoords.length === 0) return

    const features = withCoords.map(s => ({
      type: 'Feature',
      properties: {
        schedule_id: s.id,
        patch_code: s.patch_code,
        assignment_status: s.assignment_status,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[
          ...s.boundary_coordinates.map(c => [parseFloat(c.lng), parseFloat(c.lat)]),
          [parseFloat(s.boundary_coordinates[0].lng), parseFloat(s.boundary_coordinates[0].lat)],
        ]],
      },
    }))

    map.current.addSource('patches', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
    })

    map.current.addLayer({
      id: 'patches-fill',
      type: 'fill',
      source: 'patches',
      paint: {
        'fill-color': ['case',
          ['==', ['get', 'assignment_status'], 'in_progress'], '#f59e0b',
          '#3b82f6'
        ],
        'fill-opacity': 0.3,
      },
    })

    map.current.addLayer({
      id: 'patches-outline',
      type: 'line',
      source: 'patches',
      paint: {
        'line-color': ['case',
          ['==', ['get', 'assignment_status'], 'in_progress'], '#d97706',
          '#2563eb'
        ],
        'line-width': 2,
      },
    })

    if (selectedScheduleId) {
      const selFeature = features.find(f => f.properties.schedule_id === selectedScheduleId)
      if (selFeature) {
        map.current.addSource('selected', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [selFeature] },
        })
        map.current.addLayer({
          id: 'selected-fill',
          type: 'fill',
          source: 'selected',
          paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.4 },
        })
        map.current.addLayer({
          id: 'selected-outline',
          type: 'line',
          source: 'selected',
          paint: { 'line-color': '#dc2626', 'line-width': 3 },
        })
      }
    }

    const allCoords = withCoords.flatMap(s =>
      s.boundary_coordinates.map(c => [parseFloat(c.lng), parseFloat(c.lat)])
    )
    if (allCoords.length > 0) {
      const lngs = allCoords.map(c => c[0])
      const lats = allCoords.map(c => c[1])
      map.current.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 60, maxZoom: 16 }
      )
    }

    const onClick = (e) => {
      const sid = e.features?.[0]?.properties?.schedule_id
      if (sid) setSelectedScheduleId(sid)
    }
    map.current.on('click', 'patches-fill', onClick)
    map.current.on('mouseenter', 'patches-fill', () => {
      if (map.current) map.current.getCanvas().style.cursor = 'pointer'
    })
    map.current.on('mouseleave', 'patches-fill', () => {
      if (map.current) map.current.getCanvas().style.cursor = ''
    })

  }, [schedules, mapLoaded, selectedScheduleId])

  const flyToSchedule = (s) => {
    setSelectedScheduleId(s.id)
    if (!map.current || !s.boundary_coordinates?.length) return
    const coords = s.boundary_coordinates
    const lngs = coords.map(c => parseFloat(c.lng))
    const lats = coords.map(c => parseFloat(c.lat))
    map.current.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 80, maxZoom: 17 }
    )
  }

  const getCenter = (s) => {
    if (!s.boundary_coordinates?.length) return null
    const lats = s.boundary_coordinates.map(c => parseFloat(c.lat))
    const lngs = s.boundary_coordinates.map(c => parseFloat(c.lng))
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500">Loading assigned patches...</p>
      </div>
    )
  }

  const hasMapToken = MAPBOX_TOKEN && MAPBOX_TOKEN !== 'YOUR_MAPBOX_TOKEN_HERE'

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate('/lantana')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Field View</h1>
          <p className="text-sm text-gray-600">
            {schedules.length} assigned patch{schedules.length !== 1 ? 'es' : ''}
          </p>
        </div>
        <Navigation className="h-6 w-6 text-indigo-600" />
      </div>

      {schedules.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">No Assigned Patches</h3>
          <p className="text-sm text-gray-500">
            You don't have any patches assigned for clearing right now.
          </p>
        </div>
      ) : (
        <>
          {hasMapToken && (
            <div className="bg-white rounded-lg shadow mb-6 overflow-hidden">
              <div ref={mapContainer} className="h-64 sm:h-80" />
            </div>
          )}

          <div className="space-y-3">
            {schedules.map(s => {
              const center = getCenter(s)
              const isSelected = selectedScheduleId === s.id
              const statusKey = s.assignment_status
              return (
                <div
                  key={s.id}
                  className={`bg-white rounded-lg shadow p-4 border-2 transition-colors ${
                    isSelected ? 'border-indigo-400' : 'border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{s.patch_code}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_COLORS[statusKey] || 'bg-gray-100 text-gray-800'}`}>
                          {STATUS_LABELS[statusKey] || statusKey}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {s.paddock_name || 'Unknown paddock'}
                        {s.location_description && <> &middot; {s.location_description}</>}
                      </p>
                    </div>
                    {s.boundary_coordinates?.length >= 3 && hasMapToken && (
                      <button
                        onClick={() => flyToSchedule(s)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        title="Show on map"
                      >
                        <MapPin className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                    {s.density && (
                      <span className="flex items-center gap-1">
                        <Leaf className="h-3.5 w-3.5" /> {s.density}
                      </span>
                    )}
                    {s.estimated_biomass_kg && (
                      <span>{s.estimated_biomass_kg} kg</span>
                    )}
                    {s.area_ha && (
                      <span>{s.area_ha} ha</span>
                    )}
                    {s.scheduled_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Target: {new Date(s.scheduled_date).toLocaleDateString()}
                      </span>
                    )}
                    {s.actual_start_date && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        Started: {new Date(s.actual_start_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {(s.latest_ndvi !== null && s.latest_ndvi !== undefined) && (
                    <div className="flex flex-wrap gap-3 text-xs mb-3 p-2 bg-gray-50 rounded-lg">
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-green-600" />
                        <span className="text-gray-500">Vegetation:</span>
                        <span className={`font-mono font-medium ${
                          parseFloat(s.latest_ndvi) < 0.2 ? 'text-red-600' :
                          parseFloat(s.latest_ndvi) < 0.4 ? 'text-yellow-600' :
                          parseFloat(s.latest_ndvi) < 0.6 ? 'text-green-600' : 'text-emerald-700'
                        }`}>
                          {parseFloat(s.latest_ndvi).toFixed(3)}
                        </span>
                      </span>
                      {s.latest_evi !== null && s.latest_evi !== undefined && (
                        <span>
                          <span className="text-gray-500">Vigour:</span>{' '}
                          <span className="font-mono">{parseFloat(s.latest_evi).toFixed(3)}</span>
                        </span>
                      )}
                      {s.latest_nbr !== null && s.latest_nbr !== undefined && (
                        <span>
                          <span className="text-gray-500">Burn Risk:</span>{' '}
                          <span className="font-mono">{parseFloat(s.latest_nbr).toFixed(3)}</span>
                        </span>
                      )}
                      {s.latest_vegetation_health && (
                        <span className={`px-1.5 py-0.5 rounded font-medium ${
                          s.latest_vegetation_health === 'stressed' ? 'bg-red-100 text-red-700' :
                          s.latest_vegetation_health === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                          s.latest_vegetation_health === 'healthy' ? 'bg-green-100 text-green-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {s.latest_vegetation_health}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {s.assignment_status === 'assigned' && (
                      <button
                        onClick={() => startMutation.mutate(s.id)}
                        disabled={startMutation.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      >
                        <Play className="h-4 w-4" />
                        Start Clearing
                      </button>
                    )}
                    {(s.assignment_status === 'in_progress' || s.assignment_status === 'assigned') && (
                      <button
                        onClick={() => { setFinishModal(s); setFinishDate(new Date().toISOString().split('T')[0]) }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        Finish Clearing
                      </button>
                    )}
                    {center && (
                      <a
                        href={`https://maps.google.com/?q=${center.lat},${center.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open in Maps
                      </a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {finishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Finish Clearing</h3>
            <p className="text-sm text-gray-600 mb-4">
              Mark <strong>{finishModal.patch_code}</strong> as cleared.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Completion Date
              </label>
              <input
                type="date"
                value={finishDate}
                onChange={(e) => setFinishDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setFinishModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => finishMutation.mutate({ scheduleId: finishModal.id, endDate: finishDate })}
                disabled={finishMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {finishMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                Mark Cleared
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
